import { useCallback, useMemo } from 'react';

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { ApiError } from '@/api/fetcher';
import {
  API,
  createCampaignMutationOptions,
  invalidateCampaignLists,
  onCampaignRemoved,
  onCampaignUpdated,
  VerifySubscribersRequest,
  VerifySubscribersResponse,
} from '@/modules/campaigns/api';
import { isEphemeralCampaignId } from '@/modules/campaigns/constants';
import { mergeSelectedCampaign } from '@/modules/campaigns/redux/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { Campaign, CampaignStatus, PartialCampaign } from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { BULK_DELETE_CONCURRENCY, runWithConcurrency } from '@/util/concurrency';

interface DeleteProps {
  campaign_id: string;
  name?: string;
}

/**
 * Bulk delete via a capped client-side fan-out of the single-campaign DELETE (there is no bulk
 * endpoint). Standalone on purpose: `useCampaigns()` would spin up the list and detail queries,
 * and its `deleteCampaignAsync` toasts once per item.
 */
export const useBulkDeleteCampaigns = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);

  const { mutateAsync: bulkDeleteCampaignsAsync, status: bulkDeleteStatus } = useMutation<
    void,
    Error,
    string[]
  >({
    // Mandatory: the app configures global mutation retries (App.tsx), and a partial-failure
    // throw would otherwise re-run the whole fan-out and double-apply the cache decrements.
    retry: false,
    mutationFn: async (campaignIds) => {
      const results = await runWithConcurrency(
        campaignIds,
        BULK_DELETE_CONCURRENCY,
        async (campaignId) => {
          try {
            await API.deleteCampaign(campaignId);
          } catch (error) {
            // Already gone server-side — the caller's intent is satisfied.
            if ((error as ApiError)?.statusCode !== 404) {
              throw error;
            }
          }
        }
      );

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          onCampaignRemoved(queryClient, accountId, campaignIds[index]);
        }
      });

      const failedCount = results.filter((result) => result.status === 'rejected').length;
      if (failedCount > 0) {
        throw new Error(t('campaigns.bulk_delete_partial', { count: failedCount }));
      }
    },
    onSuccess: (_resp, campaignIds) => {
      toast.success(t('campaigns.bulk_deleted', { count: campaignIds.length }));
    },
    meta: { errorToast: true },
    // The refetch is the authority on page repacking and totals — run it on success too.
    onSettled: () => invalidateCampaignLists(queryClient, accountId),
  });

  return { bulkDeleteCampaignsAsync, bulkDeleteStatus };
};

const RUNNABLE_CAMPAIGN_STATUSES: CampaignStatus[] = ['draft', 'ready', 'in_progress'];

/**
 * Campaigns that can still run with the AI agent, so deleting the agent would break them.
 * `isUnchecked` is true when the list cannot be trusted: no read permission or a failed request.
 */
export const useRunnableCampaignsByBrain = (brainId: string) => {
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.READ)
  );

  const queries = useQueries({
    queries: RUNNABLE_CAMPAIGN_STATUSES.map((status) => ({
      queryKey: [
        endpoints.campaigns(undefined, undefined, undefined, undefined, status, undefined, [
          brainId,
        ]),
      ],
      queryFn: () =>
        API.listAllCampaigns(undefined, undefined, undefined, undefined, status, undefined, [
          brainId,
        ]),
      enabled: canRead,
    })),
  });

  const campaigns = queries.flatMap((query) => query.data?.campaigns ?? []);
  const isLoading = canRead && queries.some((query) => query.isLoading);
  const isUnchecked = !canRead || queries.some((query) => query.isError);

  return { campaigns, isLoading, isUnchecked };
};

export const useCampaigns = (
  campaignId?: string,
  filter?: string,
  limit?: number,
  sort?: string,
  status?: string,
  channels?: string[],
  aiAgents?: string[]
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const accountId = useSelector(selectAccountId);

  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.READ)
  );

  const campaignsQueryKey = useMemo(
    () => [endpoints.campaigns, filter, limit, accountId, sort, status, channels, aiAgents],
    [filter, limit, accountId, sort, status, channels, aiAgents]
  );

  const {
    data: campaigns,
    status: listStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: campaignsQueryKey,
    queryFn: async ({ pageParam }) => {
      return API.listAllCampaigns(pageParam, filter, limit, sort, status, channels, aiAgents);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.pagination?.has_more) {
        return { cursor: lastPage.pagination.next_cursor };
      }
      return undefined;
    },
    initialPageParam: {} as { cursor?: string },
    enabled: !!accountId && canRead,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
    placeholderData: keepPreviousData,
  });

  const isSentinel = isEphemeralCampaignId(campaignId);

  const { data: campaignData, status: rawGetStatus } = useQuery<Campaign, Error>({
    queryKey: [endpoints.campaign(campaignId)],
    queryFn: () => API.getCampaign(campaignId),
    enabled: !!campaignId && !isSentinel && canRead,
    staleTime: 2 * 60 * 1000, // 2 minutes in milliseconds
  });

  // The sentinel has no `campaigns` row. A disabled query stays status:'pending'
  // (fetchStatus:'idle') forever in v5, which would trap route-param pages that gate a
  // skeleton on getStatus === 'pending'. Report 'error' to mirror today's post-404 settled
  // state — zero behavior change there, no perpetual skeleton. MOV-16599.
  const getStatus: typeof rawGetStatus = isSentinel ? 'error' : rawGetStatus;

  const campaign = campaignData;
  const {
    mutate: updateCampaign,
    mutateAsync: updateCampaignAsync,
    status: updateStatus,
  } = useMutation<Campaign, Error, PartialCampaign>({
    mutationFn: API.updateCampaign,
    onSuccess: (resp) => {
      onCampaignUpdated(queryClient, resp);
      // Keep Redux selectedCampaign in sync so selectors like
      // selectHasNbaConfig don't read stale data after an update
      dispatch(mergeSelectedCampaign(resp));
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: createCampaignAsync, status: createStatus } = useMutation(
    createCampaignMutationOptions(queryClient)
  );

  const {
    mutate: deleteCampaign,
    mutateAsync: deleteCampaignAsync,
    status: deleteStatus,
  } = useMutation<Campaign, Error, DeleteProps>({
    mutationFn: ({ campaign_id }) => API.deleteCampaign(campaign_id),
    onSuccess: (resp, variables) => {
      onCampaignRemoved(queryClient, accountId, resp.campaign_id);
      toast.success(t('campaigns.deleted', { 0: variables.name }));
    },
    meta: { errorToast: true },
  });

  const flatCampaigns = useMemo(
    () => campaigns?.pages?.flatMap((page) => page.campaigns),
    [campaigns?.pages]
  );

  const totalRows = useMemo(() => campaigns?.pages[0]?.pagination?.total, [campaigns?.pages]);

  const campaignsByStatus = useCallback(
    (status: string) => {
      return flatCampaigns?.filter((c) => c.status === status);
    },
    [flatCampaigns]
  );

  const campaignNames = useMemo(
    () => flatCampaigns?.map((c) => c.name.toLowerCase()),
    [flatCampaigns]
  );

  const {
    mutate: verifySubscribers,
    mutateAsync: verifySubscribersAsync,
    status: verifyStatus,
  } = useMutation<VerifySubscribersResponse, Error, VerifySubscribersRequest>({
    mutationFn: API.verifySubscribers,
  });

  return {
    campaigns: campaigns?.pages || [],
    flatCampaigns,
    campaign,
    getStatus,
    listStatus,
    createStatus,
    deleteStatus,
    updateStatus,
    verifyStatus,
    campaignNames,
    createCampaignAsync,
    deleteCampaign,
    deleteCampaignAsync,
    updateCampaign,
    updateCampaignAsync,
    campaignsByStatus,
    verifySubscribers,
    verifySubscribersAsync,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalRows,
  };
};
